// Response records of one matrix field → chart data per measure moment.
//
// Lifted out of useMatrixData so the join can be tested: that hook has no tests,
// and every matrix chart (MatrixMoment*, MatrixDevelopment*, radar, growth) reads
// its output.
//
// Records are matched to rows with resolveRecordRow (RowID, then the server's
// current row title, then the frozen RowTitle). The output stays keyed by the
// row's CURRENT title, so the chart components need no change: an answer given
// under "Ik voel mij comfortabel in mijn woning" lands on the row that now reads
// "Ik voel mij prettig in mijn woning".
//
// AI-CONTEXT: See docs/features/matrix-rowid-join.md
// AI-CONTEXT: See docs/pitfalls.md#growth-dual-calculation

import { buildRowLookup } from '../../analysisHelpers/matrixRowLookup'
import resolveRecordRow from '../../analysisHelpers/resolveRecordRow'

/**
 * @param {object} params
 * @param {Array<object>} params.records - QuestionnairesResponses records (with MomentPosition)
 * @param {Array<object>} params.rowDefs - This field's MatrixQuestionRows
 * @param {number} params.lowerScale
 * @param {number} params.upperScale
 * @param {object|null} params.fieldData - The QuestionnaireFields doc (Standard decides the collapse)
 * @param {(position: number) => string} params.momentLabel - x-axis label for a moment position
 * @returns {{data: Array<object>, rowData: Array<object>, totalResponses: number, isCategorical: boolean, exposedRowDefs: Array<object>}}
 */
const aggregateMatrixByMoment = ({ records, rowDefs, lowerScale, upperScale, fieldData, momentLabel }) => {
    const lookup = buildRowLookup(rowDefs || [])
    const rowsLength = rowDefs?.length || 1

    // Group records by MomentPosition
    const byPos = {}
    ;(records || []).forEach((r) => {
        const pos = r.MomentPosition != null
            ? (typeof r.MomentPosition === 'number' ? r.MomentPosition : parseInt(r.MomentPosition, 10))
            : null
        if (pos != null) {
            ;(byPos[pos] ||= []).push(r)
        }
    })
    const positions = Object.keys(byPos).map(Number).sort((a, b) => a - b)

    // Build chart data (aggregate per row per moment)
    const dataArray = positions.map((pos) => {
        const responses = byPos[pos]

        // Accumulate per row, keyed by the row's current title
        const columnData = {}
        responses.forEach((resp) => {
            const row = resolveRecordRow(lookup, resp)
            // An answer to a row that no longer exists in this field was never
            // shown; it still is not.
            if (!row) return

            const raw = resp.Input
            const num = Number(raw)
            const isNumeric = !Number.isNaN(num)
            const isNegative = row.posNeg === 'negative'

            const bucket = (columnData[row.Title] ||= {
                sum: 0,
                count: 0,
                counts: {},
                negative: isNegative,
            })

            if (isNumeric) {
                // Flip negative rows so higher = better
                const adjusted = isNegative ? (lowerScale + upperScale - num) : num
                bucket.sum += adjusted
                bucket.count += 1
            } else {
                bucket.counts[raw] = (bucket.counts[raw] || 0) + 1
            }
        })

        // Compose the row for charts. Colors are injected at render time
        // by MatrixData via resolveColor().
        const out = { name: momentLabel(pos) }
        ;(rowDefs || []).forEach(({ Title }) => {
            const e = columnData[Title] || {}
            if (e.count > 0) {
                out[Title] = {
                    value: e.sum / e.count,
                    negative: e.negative,
                }
            } else if (Object.keys(e.counts || {}).length > 0) {
                out[Title] = {
                    counts: e.counts,
                    negative: e.negative,
                }
            } else {
                out[Title] = { value: null, counts: null, negative: null }
            }
        })

        return out
    })

    // When Standard === 'true', average all row scores into one value per moment.
    //
    // IMPORTANT: only include rows that have numeric data at EVERY moment.
    // If we averaged per-moment "whatever rows happen to be present", a row
    // that exists in moment 1 but not moment 2 would contribute to one average
    // but not the other — the delta would then compare different sample sets
    // and report spurious growth or decline.
    const isStandard = fieldData?.Standard === 'true'
    const allRowKeys = dataArray.length > 0
        ? Object.keys(dataArray[0]).filter(k => k !== 'name')
        : []
    const universalRows = allRowKeys.filter(key =>
        dataArray.every(moment => typeof moment[key]?.value === 'number')
    )

    const finalData = isStandard
        ? dataArray.map(moment => {
            if (universalRows.length === 0) {
                return { name: moment.name, Gemiddelde: { value: null, negative: false } }
            }
            const sum = universalRows.reduce((s, key) => s + moment[key].value, 0)
            const avg = sum / universalRows.length
            return { name: moment.name, Gemiddelde: { value: avg, negative: false } }
        })
        : dataArray

    const totalResponses = (records || []).length / rowsLength

    // For Standard mode, expose a synthetic single-row def
    const exposedRowDefs = isStandard
        ? [{ ID: 'gemiddelde', Title: 'Gemiddelde' }]
        : (rowDefs || [])

    const isCategorical = finalData.some(item =>
        Object.keys(item).some(key => item[key]?.counts)
    )

    // rowData = the un-collapsed per-row-per-moment array, BEFORE the Standard
    // 'Gemiddelde' collapse. Already mirrored (negative rows flipped). Radar
    // charts need one axis per real row question, which `data` throws away for
    // MSI/Standard fields. AI-CONTEXT: See docs/features/msi-effect-radar-chart.md
    return { data: finalData, rowData: dataArray, totalResponses, isCategorical, exposedRowDefs }
}

export default aggregateMatrixByMoment
